import { type FC, useState, type ReactNode, type MouseEventHandler } from 'react';
import { createFragmentContainer, graphql } from 'react-relay/legacy';
import { FormattedMessage, FormattedNumber } from 'dibs-react-intl';
import { ExpandingArea } from 'dibs-elements/exports/ExpandingArea';
import { Checkbox } from 'dibs-elements/exports/Checkbox';
import { Link } from 'dibs-elements/exports/Link';
import { SeoLink } from 'dibs-elements/exports/SeoLink';
import { type ArrayElement } from 'dibs-ts-utils/exports/ArrayElement';

import { createFacetUrlPath } from '../shared/helpers/photosFacetFilterUrlPathHelper';
import {
    trackFilterClick,
    trackFacetLinkClick,
    trackFacetClick,
} from './helpers/photosSearchBrowseTracking';

import styles from './PhotosSearchBrowseFacetBlock.scss';

import { type PhotosSearchBrowseFacetBlock_facets$data as PhotosSearchBrowseFacetBlockFacets } from './__generated__/PhotosSearchBrowseFacetBlock_facets.graphql';
import {
    type FacetType,
    type ModifyFacetHandler,
    type FacetSearchPayload,
    type FacetSearchPayloadValues,
} from './types';

const FACET_LIMIT = 5;

type FacetCheckboxProps = {
    url: string;
    isChecked: boolean;
    facet: ArrayElement<PhotosSearchBrowseFacetBlockFacets>;
    facetType: FacetType;
    name: string;
    onChange: ModifyFacetHandler;
};

const FacetCheckbox: FC<FacetCheckboxProps> = ({
    url,
    isChecked,
    onChange,
    facetType,
    name,
    facet,
}) => {
    const facetName = facet?.name || '';
    const facetLabel = facet?.label || '';
    return (
        <div className={styles.facetCheckbox}>
            <SeoLink
                linkData={{
                    path: url,
                    isLinkable: !url.includes('?'),
                }}
                className={styles.checkboxLink}
                dataTn="photos-facet-link"
            >
                <Checkbox
                    checked={isChecked}
                    dataTn="photos-facet-checkbox"
                    name={name || ''}
                    onChange={checked => {
                        trackFilterClick({
                            checked,
                            facetType,
                            filter: facetName.toLowerCase(),
                        });
                        onChange({
                            checked,
                            facetType,
                            value: facetLabel,
                            label: facetName,
                        });
                    }}
                    labelClass={styles.checkboxLabel}
                    value={facetLabel}
                    label={facetName}
                />
            </SeoLink>
            <div className={styles.facetNumber}>
                <FormattedNumber value={facet?.count || 0} />
            </div>
        </div>
    );
};

type PhotosSearchBrowseFacetBlockProps = {
    originalUrl?: string;
    facetSearch: FacetSearchPayload;
    facetType: FacetType;
    facets: PhotosSearchBrowseFacetBlockFacets;
    checkedFacets: FacetSearchPayloadValues;
    label: ReactNode;
    onChange: ModifyFacetHandler;
};

const PhotosSearchBrowseFacetBlockComponent: FC<PhotosSearchBrowseFacetBlockProps> = ({
    originalUrl,
    facetSearch,
    label,
    facetType,
    facets,
    checkedFacets,
    onChange,
}) => {
    const [isExpanded, setIsExpanded] = useState(true);
    const [isMoreExpanded, setIsModeExpanded] = useState(false);
    const additionalFacets: ReactNode[] = [];

    const toggleExpand = (): void => {
        trackFacetClick({ facetType, isExpanded });
        setIsExpanded(!isExpanded);
    };

    const toggleMoreExpand: MouseEventHandler<HTMLAnchorElement> = () => {
        trackFacetLinkClick({ facetType, isExpanded: isMoreExpanded });
        setIsModeExpanded(!isMoreExpanded);
    };

    return (
        <ExpandingArea
            showArrow
            expanded={isExpanded}
            wrapperClass={styles.expandingArea}
            onClick={toggleExpand}
            label={label}
            labelClass={styles.header}
            dataTn="photos-facet-expanding-area"
        >
            <div className={styles.facetsWrapper}>
                {(facets || []).map((facet, index) => {
                    const facetLabel = facet?.label || '';
                    const facetName = facet?.name || '';
                    const facetKey = `facet-${facetType}-${index}-${facetLabel}`;
                    const isChecked = !!checkedFacets.find(
                        checkedFacet => checkedFacet.value === facetLabel
                    );
                    const facetNode = (
                        <FacetCheckbox
                            key={facetKey}
                            onChange={onChange}
                            isChecked={isChecked}
                            facetType={facetType}
                            name={facetKey}
                            url={createFacetUrlPath({
                                originalUrl,
                                facetSearch,
                                facetType,
                                value: facetLabel,
                                label: facetName,
                                remove: isChecked,
                            })}
                            facet={facet}
                        />
                    );
                    if (index < FACET_LIMIT) {
                        return facetNode;
                    }

                    additionalFacets.push(facetNode);
                    return null;
                })}

                {additionalFacets.length > 0 && (
                    <>
                        <ExpandingArea expanded={isMoreExpanded}>{additionalFacets}</ExpandingArea>
                        <Link
                            className={styles.facetsExpandLink}
                            onClick={toggleMoreExpand}
                            dataTn="photos-facet-expand-cta"
                        >
                            {isMoreExpanded ? (
                                <FormattedMessage
                                    id="abt.PhotosSearchBrowseFacetBlock.seeLess"
                                    defaultMessage="See Less"
                                />
                            ) : (
                                <FormattedMessage
                                    id="abt.PhotosSearchBrowseFacetBlock.seeMore"
                                    defaultMessage="See More"
                                />
                            )}
                        </Link>
                    </>
                )}
            </div>
        </ExpandingArea>
    );
};

export const PhotosSearchBrowseFacetBlock = createFragmentContainer(
    PhotosSearchBrowseFacetBlockComponent,
    {
        facets: graphql`
            fragment PhotosSearchBrowseFacetBlock_facets on FacetValueType @relay(plural: true) {
                count
                label
                name
            }
        `,
    }
);
